import React, { useState, useMemo } from "react";
import "flag-icons/css/flag-icons.min.css";
import { ChevronDown, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

/**
 * Country combobox with flag icons + typeahead search.
 *
 * Uses Radix Popover + cmdk (Command) for keyboard nav + a11y, but we bypass
 * cmdk's built-in filtering (`shouldFilter={false}`) and do the filtering
 * ourselves. Two reasons:
 *   1. cmdk 1.x has quirks with async items + `filter` return-value scoring
 *      that occasionally hide every item (root cause of the "empty
 *      nationality dropdown" bug reported on production).
 *   2. Manual filtering lets us match on BOTH country name and ISO-2 code
 *      cleanly (e.g. typing "IT" or "italy" both match Italy).
 *
 * options: [{code, name}]
 */
export default function FlagSelect({
  value,
  onChange,
  options,
  placeholder = "Select…",
  ariaLabel,
  testId,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const selected = options.find((o) => o.code === value) || null;

  const filteredOptions = useMemo(() => {
    const s = (search || "").trim().toLowerCase();
    if (!s) return options;
    return options.filter((o) => {
      const name = (o.name || "").toLowerCase();
      const code = (o.code || "").toLowerCase();
      return name.includes(s) || code.includes(s);
    });
  }, [options, search]);

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setSearch("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId ? `${testId}-button` : undefined}
          className="w-full flex items-center justify-between gap-2 border-2 border-[#e2e8f0] rounded-md px-3 py-2 text-sm bg-white text-left focus:outline-none focus:border-[#2563eb] disabled:opacity-60"
        >
          <span className="flex items-center gap-2 min-w-0">
            {selected ? (
              <>
                <span className={`fi fi-${selected.code.toLowerCase()} shrink-0`} aria-hidden />
                <span className="truncate">{selected.name}</span>
              </>
            ) : (
              <span className="text-[#94a3b8] truncate">{placeholder}</span>
            )}
          </span>
          <ChevronDown className={`w-4 h-4 text-[#64748b] transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="p-0 w-[--radix-popover-trigger-width] min-w-[240px] z-[70] shadow-lg"
        data-testid={testId ? `${testId}-listbox` : undefined}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search country…"
            value={search}
            onValueChange={setSearch}
            data-testid={testId ? `${testId}-search` : undefined}
          />
          <CommandList className="max-h-64">
            {filteredOptions.length === 0 && (
              <CommandEmpty>No country found.</CommandEmpty>
            )}
            {filteredOptions.length > 0 && (
              <CommandGroup>
                {filteredOptions.map((o) => {
                  const isSelected = o.code === value;
                  return (
                    <CommandItem
                      key={o.code}
                      // Unique per-item value; ensures cmdk's internal
                      // index treats items as distinct even though we
                      // don't rely on its filtering.
                      value={o.code}
                      onSelect={() => {
                        onChange(o.code);
                        setOpen(false);
                        setSearch("");
                      }}
                      data-testid={testId ? `${testId}-option-${o.code}` : undefined}
                      className="cursor-pointer aria-selected:bg-[#eff6ff]"
                    >
                      <span className={`fi fi-${o.code.toLowerCase()} shrink-0`} aria-hidden />
                      <span className="truncate flex-1 text-[#0f1b2d]">{o.name}</span>
                      {isSelected && <Check className="w-3.5 h-3.5 text-[#2563eb] shrink-0" />}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
